/**
 * CompareIndexPage — /compare
 *
 * Public-facing index of all Crown & Rose vs competitor comparison pages.
 * Fetches published pages from GET /api/compare and renders a clean card
 * grid. The full CompetitorMatrix is included at the bottom for at-a-glance
 * scanning by visitors, bots, and AI citation engines.
 *
 * SEO / GEO: CollectionPage + ItemList JSON-LD, robots max-snippet, OG tags.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { ChevronRight, Loader2, ArrowRight, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { comparisonApi } from '@/lib/api';
import CompetitorMatrix from '@/components/comparison/CompetitorMatrix';
import PublicNavBar from '@/components/landing/PublicNavBar';

const SITE_URL = (process.env.REACT_APP_BACKEND_URL || 'https://crownnrose.com').replace('/api', '');

function safeJsonLd(obj) {
  return JSON.stringify(obj)
    .replace(/</g, '\\u003c')
    .replace(/>/g, '\\u003e')
    .replace(/&/g, '\\u0026');
}

// Static competitor descriptions — C&R-first framing: one line acknowledges
// what writers look for in that tool, then pivots immediately to why they
// don't need it when using Crown & Rose.
const COMP_BLURBS = {
  scrivener:     'Writers who reach for Scrivener want manuscript organisation. Crown & Rose delivers that plus AI drafting, KDP publishing frameworks, and one-click editing — all inside a single subscription. No switching, no add-ons.',
  atticus:       'Writers who reach for Atticus want formatting and publishing in one place. Crown & Rose goes further: the same KDP-ready export pipeline wrapped inside a complete AI writing and editing studio — one subscription.',
  vellum:        'Writers who reach for Vellum want gorgeous ebook and print formatting. Crown & Rose covers KDP-ready EPUB and print-trim export while also handling AI drafting, KDP compliance, continuity, arc analysis and marketing — one subscription.',
  prowritingaid: 'Writers who reach for ProWritingAid want deep grammar and style feedback. Crown & Rose layers that quality control directly into the production workflow alongside AI drafting, developmental editing and KDP frameworks — no AI knowledge needed.',
  jasper:        'Writers who reach for Jasper want fast AI copy. Crown & Rose is purpose-built for fiction: bulk chapter drafting, developmental editing, KDP publishing frameworks, continuity and a full book-marketing suite — one subscription, all tools.',
  novelcrafter:  'Writers who reach for Novelcrafter want structured planning and a Codex. Crown & Rose includes all of that plus bulk generation, Edit Walker, arc analysis, KDP frameworks and marketing — no BYOK, no AI expertise required.',
  sudowrite:     'Writers who reach for Sudowrite want creative inspiration and prose variety. Crown & Rose delivers systematic production control: guided frameworks, Edit Walker, continuity audit, KDP publishing and marketing — without learning AI.',
  novelai:       'Writers who reach for NovelAI want fast, unrestricted fiction continuation. Crown & Rose adds structural tools, multi-model support, KDP publishing frameworks, developmental editing and marketing — all inside one platform.',
  grammarly:     'Writers who reach for Grammarly want clean, polished prose. Crown & Rose wraps that quality level inside a complete novel-production system — AI drafting, editing, KDP frameworks, continuity and publishing. One subscription, no add-ons.',
};

function CompCard({ page }) {
  const key = page.slug?.replace('crown-and-rose-vs-', '') || '';
  const blurb = page.summary_verdict || COMP_BLURBS[key] || page.meta_description || '';
  const shortBlurb = blurb.length > 160 ? blurb.slice(0, 157) + '…' : blurb;

  return (
    <Link
      to={`/compare/${page.slug}`}
      data-testid={`compare-card-${key}`}
      className="group flex flex-col rounded-2xl border border-border bg-card p-5 shadow-sm transition hover:border-rose-300 hover:shadow-md no-underline"
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-widest text-rose-700 mb-1">
            Crown &amp; Rose vs
          </p>
          <h2 className="text-[18px] font-bold text-foreground leading-tight">
            {page.competitor_name}
          </h2>
        </div>
        <span className="shrink-0 mt-0.5 flex h-8 w-8 items-center justify-center rounded-full bg-rose-50 border border-rose-200 text-rose-700 group-hover:bg-rose-100 transition">
          <ArrowRight className="h-4 w-4" />
        </span>
      </div>
      <p className="text-[13px] text-muted-foreground leading-relaxed flex-1">{shortBlurb}</p>
      <span className="mt-4 text-[12px] font-semibold text-rose-700 group-hover:underline">
        Read full comparison →
      </span>
    </Link>
  );
}

function PendingCard({ comp }) {
  return (
    <div
      data-testid={`compare-pending-${comp.key}`}
      className="flex flex-col rounded-2xl border border-dashed border-border bg-muted/20 p-5"
    >
      <p className="text-[11px] font-bold uppercase tracking-widest text-muted-foreground mb-1">
        Crown &amp; Rose vs
      </p>
      <h2 className="text-[18px] font-bold text-muted-foreground leading-tight mb-3">{comp.name}</h2>
      <p className="text-[12.5px] text-muted-foreground flex-1">{COMP_BLURBS[comp.key] || 'Comparison coming soon.'}</p>
      <span className="mt-4 inline-flex items-center gap-1 text-[11.5px] text-muted-foreground">
        <Loader2 className="h-3 w-3" /> Coming soon
      </span>
    </div>
  );
}

export default function CompareIndexPage() {
  const [pages,   setPages]   = useState([]);
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    comparisonApi.listPublic()
      .then(({ pages: p = [], pending: pnd = [] }) => {
        setPages(p);
        setPending(pnd);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const pageUrl = `${SITE_URL}/compare`;

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'CollectionPage',
        '@id':   `${pageUrl}#webpage`,
        url:         pageUrl,
        name:        'One Subscription, Every Tool — Crown & Rose vs All Competitors',
        description: 'Framework-driven novel production: Plot Blueprint, KDP Publishing Framework, Edit Walker, Manuscript Polish, Continuity Audit, bulk drafting and marketing — one subscription at $39/mo. No AI expertise required. Replaces Scrivener, Vellum, Novelcrafter, Sudowrite, Grammarly and more.',
        inLanguage:  'en-US',
        isPartOf: { '@type': 'WebSite', url: SITE_URL, name: 'Crown & Rose' },
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Crown & Rose', item: SITE_URL },
          { '@type': 'ListItem', position: 2, name: 'Comparisons',  item: pageUrl },
        ],
      },
      {
        '@type': 'ItemList',
        name: 'Crown & Rose comparison pages',
        numberOfItems: pages.length,
        itemListElement: pages.map((p, i) => ({
          '@type':    'ListItem',
          position:   i + 1,
          name:       p.title || `Crown & Rose vs ${p.competitor_name}`,
          url:        `${SITE_URL}/compare/${p.slug}`,
          description: p.summary_verdict || p.meta_description || '',
        })),
      },
    ],
  };

  return (
    <>
      <Helmet>
        <title>Why Crown &amp; Rose Authors Need No Other Tool — Full Comparison</title>
        <meta name="description" content="One subscription. Every tool. No AI expertise needed. Crown & Rose guides authors through framework-driven workflows — KDP Publishing Framework, Edit Walker, Plot Blueprint, Continuity Audit — replacing Scrivener, Vellum, Novelcrafter, Grammarly, Sudowrite and more." />
        <meta name="keywords" content="crown and rose vs scrivener, vs vellum, vs grammarly, vs novelcrafter, vs sudowrite, vs novelai, kdp framework, ai novel writing, best ai writing tool indie authors, one platform write edit publish" />
        <link rel="canonical" href={pageUrl} />
        <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large" />
        <meta property="og:type"        content="website" />
        <meta property="og:title"       content="One Subscription, Every Tool — Crown & Rose vs All Competitors" />
        <meta property="og:description" content="Framework-driven novel production: authors follow guided workflows, never craft AI prompts. KDP Publishing Framework, Edit Walker, bulk drafting, continuity and marketing — one platform, $39/mo." />
        <meta property="og:url"         content={pageUrl} />
        <meta property="og:site_name"   content="Crown & Rose" />
        <meta name="twitter:card"       content="summary_large_image" />
        <meta name="twitter:title"      content="Crown & Rose — One Subscription, Every Tool" />
        <meta name="twitter:description" content="Framework-driven writing. You write — Crown & Rose handles the AI. From plot to published in one platform." />
        <script type="application/ld+json">{safeJsonLd(jsonLd)}</script>
      </Helmet>

      <PublicNavBar
        onSignIn={() => { window.location.href = '/login'; }}
        onSignUp={() => { window.location.href = '/'; }}
      />

      <div className="min-h-screen bg-background">
        {/* Breadcrumb */}
        <nav aria-label="Breadcrumb" className="border-b border-border bg-card">
          <div className="max-w-5xl mx-auto px-4 py-3 flex items-center gap-2 text-[12px] text-muted-foreground">
            <Link to="/" className="hover:text-foreground transition-colors">Crown &amp; Rose</Link>
            <ChevronRight className="h-3 w-3" aria-hidden="true" />
            <span className="text-foreground" aria-current="page">Comparisons</span>
          </div>
        </nav>

        <main className="max-w-5xl mx-auto px-4 py-12 space-y-14">
          {/* Hero */}
          <section className="text-center space-y-4">
            <div className="inline-flex items-center gap-1.5 rounded-full bg-rose-100 border border-rose-200 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-rose-800">
              One Subscription — All Tools — No AI Expertise Needed
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold tracking-tight">
              Write Your Novel.<br className="hidden sm:block" /> Crown &amp; Rose Handles the Rest.
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Our framework-driven platform guides you through Plot Blueprint, KDP Publishing
              Framework, Edit Walker, Continuity Audit and more — no AI knowledge required.
              Every comparison below shows how Crown &amp; Rose replaces the tool you were using,
              at a single monthly price with nothing to add.
            </p>
            <Button asChild className="gap-1.5">
              <Link to="/"><Sparkles className="h-4 w-4" /> Start writing free today</Link>
            </Button>
          </section>

          {/* Comparison cards */}
          <section aria-labelledby="comparisons-heading">
            <h2 id="comparisons-heading" className="text-2xl font-bold mb-2">
              What are you replacing?
            </h2>
            <p className="text-[13px] text-muted-foreground mb-6">
              Select any tool below to see exactly how Crown &amp; Rose covers the same need — and more.
            </p>

            {loading ? (
              <div className="flex items-center gap-2 text-muted-foreground py-8">
                <Loader2 className="h-5 w-5 animate-spin" /> Loading comparisons…
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {pages.map((p) => <CompCard key={p.slug} page={p} />)}
                {pending.map((c) => <PendingCard key={c.key} comp={c} />)}
              </div>
            )}
          </section>

          {/* Divider */}
          <div className="border-t border-border" />

          {/* Full multi-competitor matrix */}
          <CompetitorMatrix />

          {/* CTA */}
          <section className="rounded-2xl border border-rose-200 bg-gradient-to-br from-rose-50 to-white p-8 text-center space-y-4">
            <h2 className="text-2xl font-bold text-rose-900">
              Stop paying for tools Crown &amp; Rose already includes.
            </h2>
            <p className="text-[14px] text-rose-800/70 max-w-xl mx-auto">
              One platform. Every tool you need to write, edit, audit, polish, format and publish
              your novel. Start free — no card required.
            </p>
            <Button asChild size="lg" className="gap-1.5">
              <Link to="/"><Sparkles className="h-4 w-4" /> Start writing free today</Link>
            </Button>
          </section>
        </main>
      </div>
    </>
  );
}
